var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Platform, View } from 'react-native';
import { SkeletonView } from '../Common/SkeletonView';
import { IconComponent } from '../Icons/IconComponent';
import { CachedImageComponent } from '../CachedImage/CachedImageComponent';
import { useColorScheme } from '../../Hooks/UseColorScheme';
const styles = {
    img: {
        width: '100%',
        height: '100%'
    },
    loader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    }
};
export const AnimatedImageView = (_a) => {
    var { removeSkeleton, fill, resizeMode, cache, fileType, imageStyle, loaderStyle, imageContainerStyle, onLoad, color, fallbackImage, image, icon, onError, containerStyle } = _a, props = __rest(_a, ["removeSkeleton", "fill", "resizeMode", "cache", "fileType", "imageStyle", "loaderStyle", "imageContainerStyle", "onLoad", "color", "fallbackImage", "image", "icon", "onError", "containerStyle"]);
    const [uri, setUri] = useState(image || fallbackImage);
    const [iconName, setIconName] = useState(icon);
    const [loading, setLoading] = useState(Platform.OS === 'web');
    const isLoaded = useRef(false);
    const colorScheme = useColorScheme();
    const handleLoadStart = useCallback(() => {
        const timeout = setTimeout(() => {
            if (!isLoaded.current && uri) {
                setLoading(true);
            }
        }, 50);
        return () => clearTimeout(timeout);
    }, []);
    const handleLoad = useCallback(() => {
        setLoading(false);
        onLoad === null || onLoad === void 0 ? void 0 : onLoad();
        isLoaded.current = true;
    }, [onLoad]);
    const handleImageError = useCallback((e) => {
        if (fallbackImage && fallbackImage !== uri) {
            setUri(fallbackImage);
            return;
        }
        setUri('');
        setLoading(false);
        onError === null || onError === void 0 ? void 0 : onError(e);
    }, [onError, fallbackImage, uri]);
    const handleIconError = useCallback((e) => {
        setIconName('');
        if (fallbackImage && fallbackImage !== uri) {
            setUri(fallbackImage);
            return;
        }
        onError === null || onError === void 0 ? void 0 : onError(e);
    }, [onError, fallbackImage, uri]);
    useEffect(() => {
        setUri(image);
        setIconName(icon);
    }, [icon, image]);
    if (uri) {
        return (_jsxs(Animated.View, { style: containerStyle, children: [_jsx(CachedImageComponent, Object.assign({ image: uri, color: color, fill: fill, imageStyle: [styles.img, imageStyle], onLoad: handleLoad, onLoadStart: handleLoadStart, onError: handleImageError, resizeMode: resizeMode || 'contain' }, props)), !removeSkeleton && (_jsx(SkeletonView, { visible: loading, containerStyle: Object.assign(Object.assign({}, styles === null || styles === void 0 ? void 0 : styles.loader), loaderStyle) }))] }));
    }
    const size = typeof (containerStyle === null || containerStyle === void 0 ? void 0 : containerStyle.height) === 'string'
        ? 18
        : containerStyle === null || containerStyle === void 0 ? void 0 : containerStyle.height;
    if (iconName) {
        return _jsx(IconComponent, Object.assign({ name: iconName, size: size, containerStyle: containerStyle, onError: handleIconError, color: color }, props));
    }
    if (removeSkeleton) {
        return null;
    }
    const style = {};
    if ((containerStyle === null || containerStyle === void 0 ? void 0 : containerStyle.height) === '100%' && containerStyle.maxHeight) {
        style.maxHeight = undefined;
        style.height = containerStyle.maxHeight;
    }
    return (_jsx(View, { style: [containerStyle, style, { backgroundColor: colorScheme.colors.empty_background_color || 'rgb(241, 244, 245)' }] }));
};
